/* import __COLOCATED_TEMPLATE__ from './content-cards.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type AddWebsiteModalService from 'embercom/services/add-website-modal-service';
import type AppService from 'embercom/services/app-service';
import type ModalService from 'embercom/services/modal-service';
import { EntityType } from 'embercom/models/data/entity-types';

export type ContentCardType =
  | 'public-article'
  | 'snippet'
  | 'internal-article'
  | 'website-sync'
  | 'see-all';

export interface ContentCardsArgs {
  onCardClick?: (contentType: ContentCardType) => void;
  showModal?: (syncSourceType: string | undefined, entityType: EntityType) => Promise<void>;
}

export interface ContentCardsSignature {
  Args: ContentCardsArgs;
}

export default class ContentCards extends Component<ContentCardsSignature> {
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare addWebsiteModalService: AddWebsiteModalService;
  @service declare appService: AppService;
  @service declare modalService: ModalService;
  @service declare intercomEventService: any;

  get contentActions() {
    return [
      { contentType: 'public-article', action: this.createArticle },
      { contentType: 'snippet', action: this.createSnippet },
      { contentType: 'internal-article', action: this.createInternalArticle },
      { contentType: 'website-sync', action: this.syncWebsite },
      { contentType: 'see-all', action: this.showAllContentModal },
    ];
  }

  @action
  async handleCardClick(contentType: ContentCardType) {
    if (this.args.onCardClick) {
      this.args.onCardClick(contentType);
      return;
    }

    await this.openContentModal(contentType);
  }

  @action
  async openContentModal(contentType: ContentCardType) {
    let contentAction = this.contentActions.find((item) => item.contentType === contentType);
    if (contentAction) {
      await contentAction.action.call(this);
    }
  }

  @action
  createArticle() {
    this.knowledgeHubService.createContent(EntityType.ArticleContent, {}, false, true);
    this.trackAnalyticsEvent('create', 'public-article');
  }

  @action
  createSnippet() {
    this.knowledgeHubService.createContent(EntityType.ContentSnippet, {}, false, true);
    this.trackAnalyticsEvent('create', 'snippet');
  }

  @action
  createInternalArticle() {
    this.knowledgeHubService.createContent(EntityType.InternalArticle, {}, false, true);
    this.trackAnalyticsEvent('create', 'internal-article');
  }

  @action
  async syncWebsite() {
    if (this.appService.app.canUseRevampPreSync) {
      this.addWebsiteModalService.openAddWebsiteSheet();
      this.trackAnalyticsEvent('create', 'website');
      return;
    }
    if (this.args.showModal) {
      await this.args.showModal('external-content', EntityType.ExternalContent);
    }
    this.trackAnalyticsEvent('create', 'website');
  }

  @action
  async showAllContentModal() {
    if (this.args.showModal) {
      await this.args.showModal(undefined, EntityType.ArticleContent);
    }
    this.trackAnalyticsEvent('create', 'see-all');
  }

  @action
  openAddNewExternalSourceSheet() {
    this.modalService.openModal(
      'react/knowledge-hub/add-new-external-source/add-new-external-source-sheet',
      {
        open: true,
        appId: this.appService.app.id,
        adminId: this.appService.app.currentAdmin.id.toString(),
        onClose: () => this.modalService.closeModal(),
      },
    );
  }

  private trackAnalyticsEvent(action: string, object?: string) {
    this.intercomEventService.trackAnalyticsEvent({
      tab: 'unified-content-landing',
      action,
      object,
      route: 'setup',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::UnifiedContent::ContentCards': typeof ContentCards;
    'knowledge-hub/unified-content/content-cards': typeof ContentCards;
  }
}
